import React, { useEffect, useState, useRef } from 'react';
import { Routes, Route, Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Header from '@/components/layout/Header';
import api from '@/utils/api';
import { Package, ShoppingBag, Settings, List, Bot, Boxes, Download, Upload, FileSpreadsheet, Users, Building2, Palette, CreditCard, Wallet, ArrowRightLeft, LayoutGrid, Search, X, ChevronLeft, ChevronRight, Trash2, Edit, Tag, DollarSign, Lock, Handshake } from 'lucide-react';
import { toast } from 'sonner';
import ProductConfigForm from '@/components/admin/ProductConfigForm';
import OptionListsManager from '@/components/admin/OptionListsManager';
import AIFittingConfig from '@/components/admin/AIFittingConfig';
import OffShelfConfigManager from '@/components/admin/OffShelfConfigManager';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import AdminOrders from '@/components/admin/AdminOrders';
import AdminUsers from '@/components/admin/AdminUsers';
import AdminTenants from '@/components/admin/AdminTenants';
import AdminBranding from '@/components/admin/AdminBranding';
import BillingPage from '@/pages/admin/BillingPage';
import PaymentsSetupPage from '@/pages/admin/PaymentsSetupPage';
import SubscriptionSettings from '@/components/admin/SubscriptionSettings';
import FulfillmentCatalog from '@/components/admin/FulfillmentCatalog';
import AdminCoupons from '@/components/admin/AdminCoupons';
import AffiliateCoupons from '@/components/admin/AffiliateCoupons';
import AdminPayouts from '@/components/admin/AdminPayouts';
import AdminSecuritySettings from '@/components/admin/AdminSecuritySettings';
import BrandAvailabilityModal from '@/components/admin/BrandAvailabilityModal';
import { useTenant } from '@/contexts/TenantContext';

const PRODUCTS_PER_PAGE = 50;
const IMPACT_GOLF_TENANT_ID = 'tenant_2a7782078b27';

function AdminProducts({ isFulfillment = false }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const { tenantPath, tenant } = useTenant();
  const isImpactGolf = tenant?.tenant_id === IMPACT_GOLF_TENANT_ID;
  const [showBrandModal, setShowBrandModal] = useState(false);

  // Filter state
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [stockFilter, setStockFilter] = useState('');
  const [page, setPage] = useState(1);

  // Selection state (keeps ids globally across pagination + filter changes)
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Bulk action modal state
  const [bulkAction, setBulkAction] = useState(null); // null | 'delete' | 'edit'
  const [bulkEditField, setBulkEditField] = useState('min_advertised_price');
  const [bulkEditValue, setBulkEditValue] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);

  // Scalar fields exposed in the bulk-edit dropdown. Must match the backend
  // allowlist `BULK_EDIT_SCALAR_FIELDS` in app/routes/products.py.
  // Pricing fields:
  //   - cost_price           → Wholesale price paid to supplier (admin-internal)
  //   - min_advertised_price → MAP, the sole customer-facing display price
  const BULK_EDIT_FIELDS = [
    { value: 'min_advertised_price', label: 'MAP — Customer Price (USD)', type: 'number' },
    { value: 'cost_price', label: 'Wholesale Price (USD)', type: 'number' },
    { value: 'stock', label: 'Stock Quantity', type: 'number' },
    { value: 'name', label: 'Name', type: 'text' },
    { value: 'manufacturer', label: 'Manufacturer', type: 'text' },
    { value: 'category', label: 'Category', type: 'text' },
    { value: 'subcategory', label: 'Subcategory', type: 'text' },
    { value: 'description', label: 'Description', type: 'text' },
    { value: 'image_url', label: 'Image URL', type: 'text' },
  ];

  useEffect(() => {
    loadProducts();
  }, []);

  // Reset to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [search, categoryFilter, brandFilter, stockFilter]);
  
  const loadProducts = async () => {
    try {
      // Use /admin/products (not /products) so the backend applies the
      // fulfillment-tenant filter that limits this admin view to the
      // tenant's own pre-owned inventory. /products is the customer-facing
      // merged endpoint and would surface the master catalog.
      const response = await api.get('/admin/products');
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await api.delete(`/admin/products/${productId}`);
      toast.success('Product deleted');
      loadProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleExportProducts = async () => {
    try {
      const response = await api.get('/products/export/csv', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'products_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Products exported successfully');
    } catch (error) {
      toast.error('Failed to export products');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/products/export/template', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'product_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleImportProducts = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/products/import/csv', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const { imported, updated, errors } = response.data;
      
      if (errors && errors.length > 0) {
        toast.warning(`Imported: ${imported}, Updated: ${updated}, Errors: ${errors.length}`);
      } else {
        toast.success(`Successfully imported ${imported} new products, updated ${updated} existing`);
      }
      
      loadProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import products');
    } finally {
      setImporting(false);
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const toggleRowSelected = (productId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const toggleSelectAllFiltered = (filteredProducts) => {
    setSelectedIds((prev) => {
      // If every filtered product is already selected, clear them; otherwise
      // add every filtered id (selecting the full search/filter result set,
      // even rows on other pagination pages).
      const ids = filteredProducts.map((p) => p.product_id);
      const allSelected = ids.length > 0 && ids.every((id) => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const openBulkDelete = () => setBulkAction('delete');
  const openBulkEdit = () => {
    setBulkEditField('min_advertised_price');
    setBulkEditValue('');
    setBulkAction('edit');
  };
  const closeBulkModal = () => {
    if (bulkBusy) return;
    setBulkAction(null);
  };

  const runBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const response = await api.post('/admin/products/bulk-delete', { product_ids: ids });
      toast.success(`Deleted ${response.data.deleted} product(s)`);
      setBulkAction(null);
      clearSelection();
      loadProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const runBulkEdit = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (bulkEditValue === '' || bulkEditValue === null || bulkEditValue === undefined) {
      toast.error('Please enter a value');
      return;
    }
    setBulkBusy(true);
    try {
      const response = await api.post('/admin/products/bulk-update', {
        product_ids: ids,
        field: bulkEditField,
        value: bulkEditValue,
      });
      toast.success(`Updated ${response.data.modified} product(s)`);
      setBulkAction(null);
      clearSelection();
      loadProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk update failed');
    } finally {
      setBulkBusy(false);
    }
  };
  
  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#334155] border-r-transparent"></div>
        <p className="mt-4 text-gray-600">Loading products...</p>
      </div>
    );
  }

  // Derive filter options from the full product list
  const categories = Array.from(new Set(products.map(p => p.category).filter(Boolean))).sort();
  const brands = Array.from(new Set(products.map(p => p.manufacturer).filter(Boolean))).sort();

  // Apply filters
  const searchLower = search.trim().toLowerCase();
  const filteredProducts = products.filter(p => {
    if (categoryFilter && p.category !== categoryFilter) return false;
    if (brandFilter && p.manufacturer !== brandFilter) return false;
    if (stockFilter === 'in' && !(p.stock > 5)) return false;
    if (stockFilter === 'low' && !(p.stock > 0 && p.stock <= 5)) return false;
    if (stockFilter === 'out' && p.stock !== 0) return false;
    if (searchLower) {
      const haystack = `${p.name || ''} ${p.manufacturer || ''}`.toLowerCase();
      if (!haystack.includes(searchLower)) return false;
    }
    return true;
  });

  const totalFiltered = filteredProducts.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / PRODUCTS_PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PRODUCTS_PER_PAGE;
  const paginatedProducts = filteredProducts.slice(pageStart, pageStart + PRODUCTS_PER_PAGE);

  const hasActiveFilters = search || categoryFilter || brandFilter || stockFilter;
  const clearFilters = () => {
    setSearch('');
    setCategoryFilter('');
    setBrandFilter('');
    setStockFilter('');
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Chivo, sans-serif' }}>{isFulfillment ? 'Pre-Owned Products' : 'Products'}</h2>
        {!isFulfillment && (
        <div className="flex items-center space-x-3">
          {/* Import/Export Controls */}
          <div className="flex items-center space-x-2 border-r border-gray-300 pr-3">
            <button 
              onClick={handleDownloadTemplate}
              className="flex items-center space-x-1 h-10 px-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-all text-sm"
              title="Download CSV Template"
            >
              <FileSpreadsheet className="h-4 w-4" />
              <span className="hidden md:inline">Template</span>
            </button>
            <button 
              onClick={handleExportProducts}
              className="flex items-center space-x-1 h-10 px-3 bg-[#4A6741] text-white font-medium rounded-lg hover:bg-[#3d5636] transition-all text-sm"
              title="Export All Products"
            >
              <Download className="h-4 w-4" />
              <span className="hidden md:inline">Export</span>
            </button>
            <label className="flex items-center space-x-1 h-10 px-3 bg-[#D4A55A] text-white font-medium rounded-lg hover:bg-[#c4954a] transition-all text-sm cursor-pointer">
              <Upload className="h-4 w-4" />
              <span className="hidden md:inline">{importing ? 'Importing...' : 'Import'}</span>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleImportProducts}
                disabled={importing}
                className="hidden"
              />
            </label>
          </div>
          <button 
            onClick={() => navigate(tenantPath('/admin/products/new'))}
            className="h-10 px-6 bg-[#334155] text-white font-bold uppercase tracking-wider rounded-lg hover:bg-[#475569] transition-all text-sm"
          >
            Add Product
          </button>
          {isImpactGolf && (
            <button
              onClick={() => setShowBrandModal(true)}
              className="h-10 px-4 bg-white border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-all text-sm"
              data-testid="manage-brand-availability-button"
            >
              Brand Availability
            </button>
          )}
        </div>
        )}
      </div>
      {isImpactGolf && (
        <BrandAvailabilityModal open={showBrandModal} onClose={() => setShowBrandModal(false)} />
      )}
      {isFulfillment && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-4 mb-4 text-sm" data-testid="fulfillment-products-disabled-notice">
          Self-managed products are not available on the fulfillment plan. Your storefront sells from the
          master catalog - manage pricing from <strong>Catalog Pricing</strong> in the sidebar.
        </div>
      )}
      
      {/* Filter bar */}
      <div className="bg-white rounded-lg border border-[#F3F4F6] p-4 mb-4 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
          {/* Search */}
          <div className="md:col-span-4 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or manufacturer..."
              className="w-full h-10 pl-10 pr-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#334155] focus:border-transparent"
              data-testid="admin-products-search"
            />
          </div>

          {/* Category */}
          <div className="md:col-span-3">
            <Select
              value={categoryFilter || 'all'}
              onValueChange={(v) => setCategoryFilter(v === 'all' ? '' : v)}
            >
              <SelectTrigger className="w-full h-10 bg-gray-50 border-gray-200" data-testid="admin-products-category-filter">
                <SelectValue placeholder="All categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {categories.map(c => (
                  <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Brand */}
          <div className="md:col-span-3">
            <Select
              value={brandFilter || 'all'}
              onValueChange={(v) => setBrandFilter(v === 'all' ? '' : v)}
            >
              <SelectTrigger className="w-full h-10 bg-gray-50 border-gray-200" data-testid="admin-products-brand-filter">
                <SelectValue placeholder="All brands" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All brands</SelectItem>
                {brands.map(b => (
                  <SelectItem key={b} value={b}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Stock */}
          <div className="md:col-span-2">
            <Select
              value={stockFilter || 'any'}
              onValueChange={(v) => setStockFilter(v === 'any' ? '' : v)}
            >
              <SelectTrigger className="w-full h-10 bg-gray-50 border-gray-200" data-testid="admin-products-stock-filter">
                <SelectValue placeholder="Any stock" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any stock</SelectItem>
                <SelectItem value="in">In stock (&gt;5)</SelectItem>
                <SelectItem value="low">Low (1-5)</SelectItem>
                <SelectItem value="out">Out of stock</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
          <p className="text-sm text-gray-600" data-testid="admin-products-result-count">
            Showing <span className="font-semibold text-gray-900">{paginatedProducts.length}</span> of{' '}
            <span className="font-semibold text-gray-900">{totalFiltered}</span>
            {totalFiltered !== products.length && (
              <span className="text-gray-500"> (filtered from {products.length})</span>
            )}
          </p>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center space-x-1 text-sm font-medium text-[#334155] hover:text-[#475569]"
              data-testid="admin-products-clear-filters"
            >
              <X className="h-4 w-4" />
              <span>Clear filters</span>
            </button>
          )}
        </div>
      </div>

      {/* Bulk actions toolbar — visible whenever at least one product is selected */}
      {selectedIds.size > 0 && (
        <div className="bg-[#334155] text-white rounded-lg p-3 mb-4 flex items-center justify-between shadow-sm" data-testid="bulk-actions-toolbar">
          <div className="text-sm font-medium">
            <span data-testid="bulk-selected-count">{selectedIds.size}</span> selected
            <button
              onClick={clearSelection}
              className="ml-3 text-xs underline hover:text-gray-200"
              data-testid="bulk-clear-selection"
            >
              Clear selection
            </button>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={openBulkEdit}
              className="flex items-center space-x-1 h-9 px-3 bg-white text-[#334155] font-medium rounded-lg hover:bg-gray-100 transition-colors text-sm"
              data-testid="bulk-edit-btn"
            >
              <Edit className="h-4 w-4" />
              <span>Edit Selected</span>
            </button>
            <button
              onClick={openBulkDelete}
              className="flex items-center space-x-1 h-9 px-3 bg-[#EF4444] text-white font-medium rounded-lg hover:bg-red-600 transition-colors text-sm"
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="h-4 w-4" />
              <span>Delete Selected</span>
            </button>
          </div>
        </div>
      )}
      <div className="bg-gray-50 rounded-lg border border-[#F3F4F6] overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-100 border-b border-[#F3F4F6]">
            <tr>
              <th className="p-4 w-12">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[#334155] cursor-pointer"
                  checked={filteredProducts.length > 0 && filteredProducts.every((p) => selectedIds.has(p.product_id))}
                  onChange={() => toggleSelectAllFiltered(filteredProducts)}
                  title={filteredProducts.length === products.length
                    ? 'Select all products'
                    : 'Select all products matching the current filters'}
                  data-testid="bulk-select-all"
                />
              </th>
              <th className="text-left p-4 text-xs font-bold text-[#334155] uppercase tracking-wider">Product</th>
              <th className="text-left p-4 text-xs font-bold text-[#334155] uppercase tracking-wider">Category</th>
              <th className="text-left p-4 text-xs font-bold text-[#334155] uppercase tracking-wider">MAP</th>
              {isImpactGolf && (
                <th className="text-left p-4 text-xs font-bold text-[#334155] uppercase tracking-wider">Wholesale</th>
              )}
              <th className="text-left p-4 text-xs font-bold text-[#334155] uppercase tracking-wider">Stock</th>
              <th className="text-right p-4 text-xs font-bold text-[#334155] uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedProducts.length === 0 ? (
              <tr>
                <td colSpan={isImpactGolf ? 7 : 6} className="p-8 text-center text-gray-500" data-testid="admin-products-empty">
                  {products.length === 0 ? 'No products yet. Add one to get started.' : 'No products match the current filters.'}
                </td>
              </tr>
            ) : paginatedProducts.map((product) => (
              <tr
                key={product.product_id}
                className={`border-b border-[#F3F4F6] hover:bg-gray-100 transition-colors ${selectedIds.has(product.product_id) ? 'bg-[#334155]/5' : ''}`}
              >
                <td className="p-4">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-[#334155] cursor-pointer"
                    checked={selectedIds.has(product.product_id)}
                    onChange={() => toggleRowSelected(product.product_id)}
                    data-testid={`bulk-select-row-${product.product_id}`}
                  />
                </td>
                <td className="p-4">
                  <div>
                    <p className="text-gray-900 font-medium">{product.name}</p>
                    <p className="text-gray-600 text-sm">{product.manufacturer}</p>
                  </div>
                </td>
                <td className="p-4 text-gray-600">{product.category}</td>
                <td className="p-4 text-gray-900 font-bold" data-testid={`product-map-${product.product_id}`}>
                  ${(product.min_advertised_price ?? 0).toFixed(2)}
                </td>
                {isImpactGolf && (
                  <td className="p-4 text-gray-700" data-testid={`product-wholesale-${product.product_id}`}>
                    {product.cost_price != null ? `$${product.cost_price.toFixed(2)}` : '—'}
                  </td>
                )}
                <td className="p-4">
                  <span className={`inline-block px-2 py-1 rounded-lg text-xs font-bold ${
                    product.stock === 0 ? 'bg-[#EF4444]/10 text-[#EF4444]' :
                    product.stock <= 5 ? 'bg-yellow-500/10 text-yellow-500' :
                    'bg-[#334155]/10 text-[#334155]'
                  }`}>
                    {product.stock}
                  </span>
                </td>
                <td className="p-4 text-right space-x-2">
                  <button 
                    onClick={() => navigate(tenantPath(`/admin/products/edit/${product.product_id}`))}
                    className="text-[#334155] hover:text-[#475569] text-sm font-medium transition-colors"
                  >
                    Edit
                  </button>
                  <button 
                    onClick={() => handleDeleteProduct(product.product_id)}
                    className="text-[#EF4444] hover:text-red-400 text-sm font-medium transition-colors"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4" data-testid="admin-products-pagination">
          <p className="text-sm text-gray-600">
            Page <span className="font-semibold text-gray-900">{currentPage}</span> of{' '}
            <span className="font-semibold text-gray-900">{totalPages}</span>
          </p>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="flex items-center space-x-1 h-9 px-3 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="admin-products-prev-page"
            >
              <ChevronLeft className="h-4 w-4" />
              <span>Prev</span>
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="flex items-center space-x-1 h-9 px-3 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="admin-products-next-page"
            >
              <span>Next</span>
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Bulk action modal — Cancel is always available (disabled only while mutation in flight) */}
      {bulkAction && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={closeBulkModal}
          data-testid="bulk-modal-backdrop"
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="bulk-modal"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {bulkAction === 'delete' ? 'Delete Selected Products' : 'Edit Selected Products'}
              </h3>
              <button
                onClick={closeBulkModal}
                disabled={bulkBusy}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                aria-label="Close"
                data-testid="bulk-modal-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Action type tabs — always show both so the admin can switch */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => !bulkBusy && setBulkAction('edit')}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${bulkAction === 'edit' ? 'bg-[#334155] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                disabled={bulkBusy}
                data-testid="bulk-modal-tab-edit"
              >
                Edit a field
              </button>
              <button
                onClick={() => !bulkBusy && setBulkAction('delete')}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${bulkAction === 'delete' ? 'bg-[#EF4444] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                disabled={bulkBusy}
                data-testid="bulk-modal-tab-delete"
              >
                Delete
              </button>
            </div>

            {bulkAction === 'delete' ? (
              <div className="mb-6">
                <p className="text-sm text-gray-700">
                  You&apos;re about to permanently delete <span className="font-bold">{selectedIds.size}</span> product{selectedIds.size === 1 ? '' : 's'}. This cannot be undone.
                </p>
              </div>
            ) : (
              <div className="space-y-4 mb-6">
                <p className="text-sm text-gray-700">
                  Update one field on <span className="font-bold">{selectedIds.size}</span> product{selectedIds.size === 1 ? '' : 's'}.
                </p>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Field</label>
                  <Select
                    value={bulkEditField}
                    onValueChange={(v) => { setBulkEditField(v); setBulkEditValue(''); }}
                    disabled={bulkBusy}
                  >
                    <SelectTrigger className="w-full h-10 bg-gray-50 border-gray-200" data-testid="bulk-modal-field-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BULK_EDIT_FIELDS.map((f) => (
                        <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">New value</label>
                  <input
                    type={BULK_EDIT_FIELDS.find((f) => f.value === bulkEditField)?.type || 'text'}
                    step={BULK_EDIT_FIELDS.find((f) => f.value === bulkEditField)?.type === 'number' ? '0.01' : undefined}
                    value={bulkEditValue}
                    onChange={(e) => setBulkEditValue(e.target.value)}
                    placeholder="Enter the new value..."
                    disabled={bulkBusy}
                    className="w-full h-10 px-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#334155]"
                    data-testid="bulk-modal-value-input"
                  />
                </div>
              </div>
            )}

            <div className="flex items-center justify-end space-x-2">
              <button
                onClick={closeBulkModal}
                disabled={bulkBusy}
                className="h-10 px-4 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors text-sm disabled:opacity-50"
                data-testid="bulk-modal-cancel"
              >
                Cancel
              </button>
              {bulkAction === 'delete' ? (
                <button
                  onClick={runBulkDelete}
                  disabled={bulkBusy}
                  className="h-10 px-4 bg-[#EF4444] text-white font-medium rounded-lg hover:bg-red-600 transition-colors text-sm disabled:opacity-60"
                  data-testid="bulk-modal-confirm-delete"
                >
                  {bulkBusy ? 'Deleting...' : `Delete ${selectedIds.size}`}
                </button>
              ) : (
                <button
                  onClick={runBulkEdit}
                  disabled={bulkBusy}
                  className="h-10 px-4 bg-[#334155] text-white font-medium rounded-lg hover:bg-[#475569] transition-colors text-sm disabled:opacity-60"
                  data-testid="bulk-modal-confirm-edit"
                >
                  {bulkBusy ? 'Updating...' : `Update ${selectedIds.size}`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminOverview() {
  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-8" style={{ fontFamily: 'Chivo, sans-serif' }}>Admin Dashboard</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        <div className="bg-gray-50 p-6 rounded-lg border border-[#F3F4F6]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-gray-600 text-sm font-bold uppercase tracking-wider">Total Orders</h3>
            <Package className="h-8 w-8 text-[#334155]" />
          </div>
          <p className="text-3xl font-bold text-gray-900">0</p>
        </div>
        
        <div className="bg-gray-50 p-6 rounded-lg border border-[#F3F4F6]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-gray-600 text-sm font-bold uppercase tracking-wider">Total Products</h3>
            <ShoppingBag className="h-8 w-8 text-[#334155]" />
          </div>
          <p className="text-3xl font-bold text-gray-900">0</p>
        </div>
        
        <div className="bg-gray-50 p-6 rounded-lg border border-[#F3F4F6]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-gray-600 text-sm font-bold uppercase tracking-wider">Revenue</h3>
            <Package className="h-8 w-8 text-[#334155]" />
          </div>
          <p className="text-3xl font-bold text-gray-900">$0.00</p>
        </div>
      </div>
      
      <div className="bg-gray-50 p-8 rounded-lg border border-[#F3F4F6] text-center">
        <p className="text-gray-600">Select a section from the sidebar to get started</p>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [subscriptionType, setSubscriptionType] = useState('self_managed');
  const { tenantPath, tenant } = useTenant();
  const [searchParams, setSearchParams] = useSearchParams();
  
  useEffect(() => {
    const checkUser = async () => {
      try {
        const response = await api.get('/auth/me');
        setUser(response.data);
      } catch (error) {
        // Expected when not logged in - just log for debugging visibility
        console.debug('Not logged in / failed to load current user:', error);
      }
    };
    const loadSub = async () => {
      try {
        const res = await api.get('/tenant/subscription');
        setSubscriptionType(res.data.subscription_type || 'self_managed');
      } catch (error) {
        // Expected when not admin/no subscription - just log for debugging visibility
        console.debug('Failed to load tenant subscription type:', error);
      }
    };
    checkUser();
    loadSub();
  }, [location.pathname]);

  // Verify subscription checkout on return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const setup = searchParams.get('setup');
    if (sessionId && setup === 'complete') {
      api.post('/subscriptions/verify-checkout', { session_id: sessionId })
        .then(() => {
          toast.success('Subscription activated! Your 7-day trial has started.');
          setSearchParams({});
        })
        .catch(() => {
          toast.error('Could not verify subscription. Please check Billing.');
          setSearchParams({});
        });
    }
  }, []);
  
  const isFulfillment = subscriptionType === 'fulfillment';
  const isImpactGolf = tenant?.tenant_id === IMPACT_GOLF_TENANT_ID;
  
  const navItems = [
    { path: tenantPath('/admin'), label: 'Overview', icon: Settings },
    ...(isFulfillment
      ? [
          { path: tenantPath('/admin/fulfillment-catalog'), label: 'Catalog Pricing', icon: LayoutGrid },
        ]
      : [
          { path: tenantPath('/admin/products'), label: 'Products', icon: ShoppingBag },
          { path: tenantPath('/admin/off-shelf'), label: 'Ready to Ship', icon: Boxes },
          { path: tenantPath('/admin/option-lists'), label: 'Option Lists', icon: List },
        ]
    ),
    { path: tenantPath('/admin/ai-fitting'), label: 'Club Fitting Wizard', icon: Bot },
    { path: tenantPath('/admin/orders'), label: 'Orders', icon: Package },
    ...((isFulfillment || isImpactGolf)
      ? [{ path: tenantPath('/admin/payouts'), label: 'Payouts', icon: DollarSign }]
      : []
    ),
    { path: tenantPath('/admin/coupons'), label: 'Coupons', icon: Tag },
    ...(isImpactGolf
      ? [{ path: tenantPath('/admin/affiliate-coupons'), label: 'Affiliate Coupons', icon: Handshake }]
      : []
    ),
    { path: tenantPath('/admin/users'), label: 'Users', icon: Users },
    { path: tenantPath('/admin/security'), label: 'Security', icon: Lock },
    { path: tenantPath('/admin/branding'), label: 'Branding', icon: Palette },
    { path: tenantPath('/admin/subscription'), label: 'Subscription & Billing', icon: CreditCard },
    { path: tenantPath('/admin/payments'), label: 'Payments Setup', icon: Wallet },
  ];
  
  // Only show Platform Admin link for superadmins
  const isSuperadmin = user?.is_superadmin || user?.is_platform_user;
  
  // Add Tenants nav item for superadmins
  if (isSuperadmin) {
    navItems.push({ path: tenantPath('/admin/tenants'), label: 'Tenants', icon: Building2 });
  }
  
  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Header />
      
      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 bg-white border-r border-[#E8E4DC] min-h-[calc(100vh-80px)] p-6">
          <h2 className="text-xl font-bold text-[#2C3E2D] mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>Admin Panel</h2>
          <nav className="space-y-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                    isActive
                      ? 'bg-[#4A6741] text-white font-bold'
                      : 'text-[#5A6B5C] hover:text-[#2C3E2D] hover:bg-[#F5F3EF]'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
            
            {/* Platform Admin link for superadmins */}
            {isSuperadmin && (
              <a
                href="/platform/admin"
                className="flex items-center space-x-3 px-4 py-3 rounded-lg transition-all text-[#5A6B5C] hover:text-[#2C3E2D] hover:bg-[#F5F3EF] border-l-2 border-[#D4A55A] mt-4"
                data-testid="nav-platform-admin"
              >
                <Building2 className="h-5 w-5" />
                <span>Platform Admin</span>
                <span className="ml-auto text-xs bg-[#D4A55A]/20 text-[#D4A55A] px-1.5 py-0.5 rounded">
                  SA
                </span>
              </a>
            )}
          </nav>
        </div>
        
        {/* Main Content */}
        <div className="flex-1 p-12">
          <Routes>
            <Route index element={<AdminOverview />} />
            <Route path="products" element={<AdminProducts isFulfillment={isFulfillment} />} />
            <Route path="products/new" element={<ProductConfigForm />} />
            <Route path="products/edit/:productId" element={<ProductConfigForm />} />
            <Route path="off-shelf" element={<OffShelfConfigManager />} />
            <Route path="option-lists" element={<OptionListsManager />} />
            <Route path="ai-fitting" element={<AIFittingConfig />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="payouts" element={<AdminPayouts />} />
            <Route path="coupons" element={<AdminCoupons />} />
            <Route path="affiliate-coupons" element={<AffiliateCoupons />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="security" element={<AdminSecuritySettings />} />
            <Route path="branding" element={<AdminBranding />} />
            <Route path="subscription" element={<SubscriptionSettings />} />
            <Route path="fulfillment-catalog" element={<FulfillmentCatalog />} />
            <Route path="billing" element={<SubscriptionSettings />} />
            <Route path="payments" element={<PaymentsSetupPage />} />
            <Route path="tenants" element={<AdminTenants />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}
